/**
 * Shared sanity rules for scenario/hospital price rows.
 *
 * Single source of truth for what counts as a garbage price (chargemaster
 * unit-filing errors — per-vial prices in per-mcg fields and the like), used
 * by useHospitalScenarios and every surface that ingests raw
 * scenario_hospital_coverage rows (coverage shards, RPCs, live queries).
 *
 * Policy (Trent): NEVER hide real outrageous prices — only dimensionally
 * invalid math. Hence the RELATIVE rule: $5M+ alone doesn't disqualify
 * (gene-therapy scenarios legitimately price past $5M); it must also be
 * >4× the scenario's own national average to read as garbage.
 */

/** True when a blended/hospital price row is unusable garbage. */
export function isGarbagePrice(
  price: number,
  nationalCost: number,
  markupPct?: number | null
): boolean {
  if (!Number.isFinite(price) || price <= 0) return true;
  // Inflated national baseline (broken $1M+ average from a bad drug-quantity
  // unit bug) makes the comparison meaningless. If a scenario's national total
  // is in the millions and the hospital reads 90%+ "cheaper", it's almost always
  // a broken baseline or a coverage artifact (the hospital only matched the cheap
  // codes), NOT a real deal — so it doesn't belong in the cheapest list.
  // (Stopgap while the backend re-audit fixes the underlying scenario quantities.)
  if (nationalCost > 1_000_000 && markupPct != null && markupPct < -90) return true;
  // Unit-bug hospital price: astronomical AND far beyond the scenario's scale.
  if (price > 5_000_000 && price > 4 * nationalCost) return true;
  return false;
}

/** Blend a raw coverage row the way the pricing model intends, guarding the
 *  null/missing `np` cases that otherwise produce NaN or double-counting.
 *  Returns null when the row can't be priced sanely. */
export function blendCoverageRow(row: {
  hp?: number | null;           // total_hospital_price
  np?: number | null;           // total_national_price (matched-codes portion)
  mc?: number | null;           // matched_codes
  tc?: number | null;           // total_codes
  pm?: string | null;           // pricing_method
  nationalCost?: number | null; // scenario real_total_cost
}): { blendedPrice: number; blendedMarkup: number | null } | null {
  const hp = Number(row.hp);
  if (!Number.isFinite(hp) || hp <= 0) return null;
  const nc = Number(row.nationalCost) || 0;
  const np = Number(row.np) || 0;
  const fullyCovered = row.mc != null && row.tc != null && row.mc >= row.tc;
  // DB writes 'drg_blended'; the static shard generator shortens it to 'drg'
  // (generate_static_cache.py). Accept both — checking only the long form
  // made this guard dead code for all shard-fed surfaces, re-adding national
  // itemized costs on top of complete DRG bundle prices (avg +$84K phantom).
  const drgBlended = row.pm === 'drg_blended' || row.pm === 'drg';
  const unmatched = (fullyCovered || drgBlended || nc <= 0)
    ? 0
    : Math.max(0, nc - np);
  const blendedPrice = hp + unmatched;
  const blendedMarkup = nc > 0 ? ((blendedPrice - nc) / nc) * 100 : null;
  if (isGarbagePrice(blendedPrice, nc, blendedMarkup)) return null;
  return { blendedPrice, blendedMarkup };
}
